/**
 * Goal form basic section — icon, name, category, year, target, unit, direction, privacy.
 */

import React, { useState, useRef, useCallback, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { EmojiClickData } from 'emoji-picker-react'
import { useCategories } from '@turnifly/shared/hooks'
import { Input } from '@turnifly/shared/ui'

const EmojiPicker = lazy(() =>
  import('emoji-picker-react').catch(() => {
    window.location.reload()
    return { default: () => null } as never
  }),
)

const FALLBACK_CATEGORIES = [
  { id: 'education', icon: '📚', name_uz: "Ta'lim", name_en: 'Education' },
  { id: 'sport', icon: '💪', name_uz: 'Sport', name_en: 'Sport' },
  { id: 'health', icon: '❤️', name_uz: "Sog'liq", name_en: 'Health' },
  { id: 'business', icon: '💰', name_uz: 'Biznes', name_en: 'Business' },
  { id: 'finance', icon: '💵', name_uz: 'Moliya', name_en: 'Finance' },
  { id: 'personal_growth', icon: '🚀', name_uz: 'Shaxsiy rivojlanish', name_en: 'Personal Growth' },
  { id: 'creativity', icon: '🎨', name_uz: 'Ijodkorlik', name_en: 'Creativity' },
  { id: 'spiritual', icon: '🕌', name_uz: 'Ruhiy', name_en: 'Spiritual' },
  { id: 'lifestyle', icon: '🌱', name_uz: 'Turmush tarzi', name_en: 'Lifestyle' },
  { id: 'tech', icon: '💻', name_uz: 'IT', name_en: 'IT' },
]

const UNIT_SUGGESTIONS = ['kitob', 'sahifa', 'km', 'kg', 'soat', 'marta', "so'm", 'ball', 'dona']

interface GoalFormBasicProps {
  icon: string
  name: string
  category: string
  year: number
  targetValue: string
  unit: string
  direction: 'increase' | 'decrease'
  isPrivate: boolean
  hideNumericFields?: boolean
  onIconChange: (v: string) => void
  onNameChange: (v: string) => void
  onCategoryChange: (v: string) => void
  onYearChange: (v: number) => void
  onTargetValueChange: (v: string) => void
  onUnitChange: (v: string) => void
  onDirectionChange: (v: 'increase' | 'decrease') => void
  onPrivateChange: (v: boolean) => void
}

export const GoalFormBasic: React.FC<GoalFormBasicProps> = ({
  icon, name, category, year, targetValue, unit, direction, isPrivate,
  hideNumericFields = false,
  onIconChange, onNameChange, onCategoryChange, onYearChange,
  onTargetValueChange, onUnitChange, onDirectionChange, onPrivateChange,
}) => {
  const { t, i18n } = useTranslation()
  const { data: categories } = useCategories()
  const dbCats = (categories ?? []) as Array<Record<string, unknown>>
  const catList = dbCats.length > 0 ? dbCats : FALLBACK_CATEGORIES
  const [isPickerOpen, setPickerOpen] = useState(false)
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const lang = i18n.language === 'uz' ? 'name_uz' : 'name_en'
  const currentYear = new Date().getFullYear()

  const openPicker = useCallback((): void => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPickerPos({ top: rect.bottom + 8, left: Math.max(8, rect.left - 100) })
    }
    setPickerOpen(true)
  }, [])

  const handleEmojiClick = useCallback((data: EmojiClickData): void => {
    onIconChange(data.emoji)
    setPickerOpen(false)
  }, [onIconChange])

  return (
    <div className="space-y-4">
      {/* Icon + Name */}
      <div className="flex items-center gap-3">
        <button
          ref={buttonRef}
          onClick={openPicker}
          className="w-12 h-12 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-2xl shrink-0 hover:bg-white/[0.1] transition-colors tap-highlight-transparent"
        >
          {icon}
        </button>
        <div className="flex-1">
          <Input
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={hideNumericFields ? t('goals.namePlaceholderSteps') : t('goals.namePlaceholder')}
            maxLength={100}
          />
        </div>
      </div>

      {/* Emoji picker portal */}
      {isPickerOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setPickerOpen(false)} />
          <div className="fixed z-[91]" style={{ top: pickerPos.top, left: pickerPos.left }}>
            <Suspense fallback={<div className="w-[300px] h-[350px] bg-surface rounded-xl animate-pulse" />}>
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                width={300}
                height={350}
                skinTonesDisabled
                searchDisabled={false}
                previewConfig={{ showPreview: false }}
              />
            </Suspense>
          </div>
        </>,
        document.body,
      )}

      {/* Category chips */}
      <div>
        <label className="text-xs font-medium text-text-hint mb-2 block">{t('goals.category')}</label>
        <div className="flex flex-wrap gap-1.5">
          {catList.map((cat) => {
            const catId = String(cat.id)
            const isSelected = category === catId
            return (
              <button
                key={catId}
                onClick={() => onCategoryChange(isSelected ? '' : catId)}
                className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors tap-highlight-transparent ${
                  isSelected
                    ? 'bg-primary/[0.15] text-primary border border-primary/30'
                    : 'bg-white/[0.04] text-text-hint border border-transparent hover:bg-white/[0.08]'
                }`}
              >
                {String(cat.icon)} {String(cat[lang] ?? cat.name_en)}
              </button>
            )
          })}
        </div>
      </div>

      {/* Year selector — custom stepper (no native select/light-mode issue) */}
      <div>
        <label className="text-xs font-medium text-text-hint mb-1.5 block">{t('goals.year')}</label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onYearChange(Math.max(currentYear, year - 1))}
            disabled={year <= currentYear}
            className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-text-hint hover:bg-white/[0.1] disabled:opacity-30 transition-colors tap-highlight-transparent"
          >
            ‹
          </button>
          <div className="flex-1 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center">
            <span className="text-sm font-semibold text-text tabular-nums">{year}</span>
          </div>
          <button
            type="button"
            onClick={() => onYearChange(Math.min(currentYear + 5, year + 1))}
            disabled={year >= currentYear + 5}
            className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-text-hint hover:bg-white/[0.1] disabled:opacity-30 transition-colors tap-highlight-transparent"
          >
            ›
          </button>
        </div>
      </div>

      {/* Target + Unit + Direction (hidden for steps mode) */}
      {!hideNumericFields && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-text-hint mb-1.5 block">{t('goals.targetValue')}</label>
              <input
                type="number"
                inputMode="numeric"
                value={targetValue}
                onChange={(e) => onTargetValueChange(e.target.value)}
                placeholder="50"
                min={1}
                className="w-full h-10 px-3 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm text-text placeholder:text-text-hint/40 outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-text-hint mb-1.5 block">{t('goals.unit')}</label>
              <input
                type="text"
                value={unit}
                onChange={(e) => onUnitChange(e.target.value)}
                placeholder={t('goals.unitPlaceholder')}
                maxLength={20}
                className="w-full h-10 px-3 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm text-text placeholder:text-text-hint/40 outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
              />
            </div>
          </div>

          {/* Unit suggestions */}
          <div className="flex flex-wrap gap-1">
            {UNIT_SUGGESTIONS.map((u) => (
              <button
                key={u}
                onClick={() => onUnitChange(u)}
                className={`px-2 py-0.5 rounded-full text-[10px] transition-colors tap-highlight-transparent ${
                  unit === u
                    ? 'bg-primary/[0.12] text-primary'
                    : 'bg-white/[0.04] text-text-hint hover:bg-white/[0.08]'
                }`}
              >
                {u}
              </button>
            ))}
          </div>

          {/* Direction */}
          <div>
            <label className="text-xs font-medium text-text-hint mb-2 block">{t('goals.direction')}</label>
            <div className="grid grid-cols-2 gap-2">
              {(['increase', 'decrease'] as const).map((dir) => (
                <button
                  key={dir}
                  onClick={() => onDirectionChange(dir)}
                  className={`py-2.5 rounded-xl text-xs font-medium border transition-colors tap-highlight-transparent ${
                    direction === dir
                      ? 'bg-primary/[0.12] text-primary border-primary/30'
                      : 'bg-white/[0.04] text-text-hint border-white/[0.06] hover:bg-white/[0.08]'
                  }`}
                >
                  {dir === 'increase' ? '📈' : '📉'} {t(`goals.direction${dir === 'increase' ? 'Increase' : 'Decrease'}`)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Privacy toggle */}
      <button
        onClick={() => onPrivateChange(!isPrivate)}
        className="w-full flex items-center justify-between py-2.5 px-3 rounded-xl bg-white/[0.04] border border-white/[0.06]"
      >
        <span className="text-xs text-text">{isPrivate ? '🔒' : '🌍'} {t('goals.private')}</span>
        <div className={`w-9 h-5 rounded-full transition-colors relative ${isPrivate ? 'bg-primary' : 'bg-white/[0.12]'}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${isPrivate ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </div>
      </button>
    </div>
  )
}
